import { useCallback, useEffect, useState } from "react";

export type ToastKind = "info" | "success" | "error";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

const AUTO_DISMISS_MS = 5_000;
let toastSeq = 0;

/**
 * 极简 toast：409 回滚、参数非法、批量成功都走这里。
 * 不引入第三方库，保持 M1 的依赖面。
 */
export function useToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const push = useCallback((kind: ToastKind, message: string) => {
    toastSeq += 1;
    setToasts((prev) => [...prev, { id: toastSeq, kind, message }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // 每次新增后重新计时：连续错误不会被第一条的定时器提前清掉。
  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = setTimeout(() => {
      setToasts((prev) => prev.slice(1));
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [toasts]);

  return { toasts, push, dismiss };
}

const KIND_STYLE: Record<ToastKind, { ink: string; bg: string; border: string }> = {
  info: { ink: "var(--ink)", bg: "var(--panel-2)", border: "var(--line-2)" },
  success: {
    ink: "var(--jade)",
    bg: "var(--jade-soft)",
    border: "rgba(143,199,166,.35)",
  },
  error: { ink: "var(--red)", bg: "var(--red-soft)", border: "rgba(223,122,102,.35)" },
};

export function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-5 left-1/2 z-50 flex w-full max-w-md -translate-x-1/2 flex-col gap-2 px-4"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className="pointer-events-auto flex items-start gap-3"
          style={{
            padding: "8px 11px",
            border: `1px solid ${KIND_STYLE[toast.kind].border}`,
            borderRadius: 8,
            background: `linear-gradient(180deg, var(--wash-1), transparent), ${KIND_STYLE[toast.kind].bg}`,
            color: KIND_STYLE[toast.kind].ink,
            fontSize: 12.5,
            boxShadow: "var(--shadow-card)",
          }}
        >
          <span className="flex-1">{toast.message}</span>
          <button
            type="button"
            aria-label="关闭提示"
            onClick={() => onDismiss(toast.id)}
            className="rounded px-1 text-xs opacity-70 hover:opacity-100"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
